import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import {
  FormArray,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { FeedbackStepData } from '../../feedback-step.interface';
import { FeedbackDrawerParams, FeedbackQuestion } from '../../../feedback';
import {
  QuestionType,
  QUESTION_SCALE_LABELS,
  QUESTION_TYPE_MAP,
} from '../../feedback-question.enum';
import { CaseBehaviour } from '../../../../../insights.interface';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BehaviorGraphComponent } from './behavior-graph/behavior-graph.component';

@Component({
  selector: 'app-behavior-metrics-step',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzRadioModule,
    NzButtonModule,
    NzFormModule,
    NzDividerModule,
    NzInputModule,
    NzSelectModule,
    BehaviorGraphComponent,
  ],
  templateUrl: './behavior-metrics-step.component.html',
  styleUrls: ['../../feedback-form.component.scss'],
})
export class BehaviorMetricsStepComponent implements OnChanges, OnDestroy {
  @Input() stepData!: FeedbackStepData;
  @Input() feedbackDetails!: FeedbackDrawerParams;
  @Input() formGroup!: FormGroup;
  @Input() onNext!: () => void;
  @Input() onPrevious!: () => void;
  @Input() overAllScore = 0;

  caseBehvaiors: CaseBehaviour[] = [];
  private destroy$ = new Subject<void>();

  ngOnChanges(): void {
    this.setupValidation();
    if (this.feedbackDetails && this.feedbackDetails.caseDetail) {
      this.caseBehvaiors =
        this.feedbackDetails.caseDetail.caseBehaviors?.filter(
          behavior => !behavior.draft && behavior.statusId !== 2
        ) || [];

      if (this.caseBehvaiors.length > 0) {
        const currentValue = this.formGroup.get('caseBehaviorMappingId')?.value;
        if (!currentValue) {
          this.formGroup.patchValue({
            caseBehaviorMappingId: this.caseBehvaiors[0].id,
          });
          this.updateBehaviorSpecificQuestions(this.caseBehvaiors[0].id);
        }
      }

      this.setupBehaviorCategorySubscription();
    }

    if (this.stepData && this.formGroup) {
      this.setupValidation();
    }
  }

  private setupBehaviorCategorySubscription(): void {
    const behaviorCategoryControl = this.formGroup.get('caseBehaviorMappingId');
    if (behaviorCategoryControl) {
      behaviorCategoryControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((selectedBehaviorId: number) => {
          if (selectedBehaviorId) {
            this.updateBehaviorSpecificQuestions(selectedBehaviorId);
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupValidation() {
    const behaviorCategoryControl = this.formGroup.get('caseBehaviorMappingId');
    if (behaviorCategoryControl && this.caseBehvaiors.length > 0) {
      if (!behaviorCategoryControl.value) {
        this.formGroup.patchValue({
          caseBehaviorMappingId: this.caseBehvaiors[0].id,
        });
      }
      const questionType =
        this.stepData.questions.length > 0
          ? this.getQuestionType(this.stepData.questions[0])
          : '';
      if (questionType !== 'text') {
        behaviorCategoryControl.setValidators([Validators.required]);
      }
      behaviorCategoryControl.updateValueAndValidity();
    }

    const questionResponses = this.formGroup.get(
      'questionResponses'
    ) as FormArray;

    this.stepData.questions.forEach((question: FeedbackQuestion) => {
      const index = this.getQuestionIndex(question.id);
      if (index >= 0) {
        const control = questionResponses.at(index);
        const questionType = this.getQuestionType(question);

        if (questionType === 'text') {
          const descriptionControl = control.get('description');
          if (descriptionControl) {
            descriptionControl.clearValidators();
            descriptionControl.updateValueAndValidity();
          }
        } else {
          const ratingScaleControl = control.get('ratingScale');
          if (ratingScaleControl) {
            ratingScaleControl.setValidators([Validators.required]);
            ratingScaleControl.updateValueAndValidity();
          }
        }

        const questionIdControl = control.get('caseFeedbackQuestionId');
        if (questionIdControl && questionIdControl.value !== question.id) {
          questionIdControl.setValue(question.id);
        }
      }
    });
  }

  private updateBehaviorSpecificQuestions(selectedBehaviorId: number): void {
    const questionResponses = this.formGroup.get(
      'questionResponses'
    ) as FormArray;

    this.stepData.questions.forEach((question: FeedbackQuestion) => {
      const index = this.getQuestionIndex(question.id);
      if (index >= 0) {
        const control = questionResponses.at(index);
        const caseBehaviorMappingId = this.isBehaviorSpecificQuestion(question)
          ? selectedBehaviorId
          : 0;

        control.patchValue({
          caseBehaviorMappingId: caseBehaviorMappingId,
        });
      }
    });
  }

  private isBehaviorSpecificQuestion(question: FeedbackQuestion): boolean {
    return question.category === 'BehaviorSpecificMetrics';
  }
  getQuestionIndex(questionId: number): number {
    const questionResponses = this.formGroup.get(
      'questionResponses'
    ) as FormArray;
    return questionResponses.controls.findIndex(
      control => control.get('caseFeedbackQuestionId')?.value === questionId
    );
  }

  getQuestionType(question: FeedbackQuestion): string {
    return QUESTION_TYPE_MAP[question.id] || QuestionType.Scale;
  }

  getScaleLabel(question: FeedbackQuestion, value: number): string {
    const labels = QUESTION_SCALE_LABELS[question.id] || [
      'Very Poor',
      'Poor',
      'Fair',
      'Good',
      'Excellent',
    ];
    return labels[value - 1] || labels[0];
  }

  getBehaviorCategoryValidationStatus(): string {
    const behaviorCategoryControl = this.formGroup.get('caseBehaviorMappingId');

    if (this.caseBehvaiors.length > 0) {
      const currentValue = behaviorCategoryControl?.value;
      if (!currentValue || currentValue === 0) {
        if (
          behaviorCategoryControl?.dirty ||
          behaviorCategoryControl?.touched ||
          this.formGroup.dirty
        ) {
          return 'error';
        }
      }
    }
    return '';
  }

  getBehaviorCategoryErrorTip(): string {
    const behaviorCategoryControl = this.formGroup.get('caseBehaviorMappingId');

    if (this.caseBehvaiors.length > 0) {
      const currentValue = behaviorCategoryControl?.value;
      if (!currentValue || currentValue === 0) {
        if (
          behaviorCategoryControl?.dirty ||
          behaviorCategoryControl?.touched ||
          this.formGroup.dirty
        ) {
          return 'Please select a behavior category';
        }
      }
    }
    return '';
  }

  getQuestionValidationStatus(questionId: number): string {
    const index = this.getQuestionIndex(questionId);
    if (index >= 0) {
      const questionResponses = this.formGroup.get(
        'questionResponses'
      ) as FormArray;
      const control = questionResponses.at(index);
      const questionType = this.getQuestionType(
        this.stepData.questions.find(q => q.id === questionId)!
      );

      let hasError = false;
      if (questionType !== 'text') {
        const ratingScale = control.get('ratingScale');
        const value = ratingScale?.value;
        hasError = value === null || value === undefined || value === 0;
      }

      if (
        hasError &&
        (control?.dirty || control?.touched || this.formGroup.touched)
      ) {
        return 'error';
      }
    }
    return '';
  }

  getQuestionErrorTip(questionId: number): string {
    const index = this.getQuestionIndex(questionId);
    if (index >= 0) {
      const questionResponses = this.formGroup.get(
        'questionResponses'
      ) as FormArray;
      const control = questionResponses.at(index);
      const question = this.stepData.questions.find(q => q.id === questionId)!;
      const questionType = this.getQuestionType(question);

      let hasError = false;
      if (questionType !== 'text') {
        const ratingScale = control.get('ratingScale');
        const value = ratingScale?.value;
        hasError = value === null || value === undefined || value === 0;

        if (
          hasError &&
          (ratingScale?.dirty || ratingScale?.touched || this.formGroup.touched)
        ) {
          return 'Answer is required';
        }
      }
    }
    return '';
  }
}
